import React, {useEffect, useRef, useState} from "react";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import ButtonBase from "@mui/material/ButtonBase";
import Typography from "@mui/material/Typography";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import {PushPin} from "@mui/icons-material";
import useMachineStore from "../../Store/MachineStore";
import usePinnedMachineStore from "../../Store/PinnedMachineStore";
import DockTile from "./DockTile";
import DockPicker from "./DockPicker";

export default function MachineDock({onOpenTasks, onLocateMachine}) {
    const machines = useMachineStore(state => state.machines)
    const pinnedMachineIds = usePinnedMachineStore(state => state.pinnedMachineIds)
    const [openPicker, setOpenPicker] = useState(false)
    const [errorMessage, setErrorMessage] = useState(null)
    const dockRef = useRef(null)

    // Les cartes lisent cette hauteur (scroll-margin-top) pour ne pas finir cachées sous la barre collée.
    useEffect(() => {
        const element = dockRef.current
        if (!element || typeof ResizeObserver === "undefined") {
            return
        }

        const observer = new ResizeObserver(() => {
            document.documentElement.style.setProperty("--machine-dock-height", `${element.offsetHeight}px`)
        })
        observer.observe(element)

        return () => {
            observer.disconnect()
            document.documentElement.style.removeProperty("--machine-dock-height")
        }
    }, []);

    const pinnedMachines = machines.filter(machine => pinnedMachineIds.includes(machine.id))

    return (
        <Paper
            ref={dockRef}
            square
            elevation={2}
            sx={{position: "sticky", top: 0, zIndex: "appBar", textAlign: "left"}}
        >
            <Box sx={{display: "flex", flexWrap: "wrap", alignItems: "stretch", gap: 1, px: 1.5, py: 1}}>
                {pinnedMachines.length === 0 &&
                    <Typography variant="body2" color="text.secondary" sx={{alignSelf: "center", px: 1}}>
                        Épinglez les machines de votre poste pour les garder en haut de l'écran.
                    </Typography>
                }

                {pinnedMachines.map(machine => (
                    <DockTile
                        key={machine.id}
                        machine={machine}
                        onOpenTasks={onOpenTasks}
                        onLocate={onLocateMachine}
                        onError={setErrorMessage}
                    />
                ))}

                <ButtonBase
                    onClick={() => setOpenPicker(true)}
                    aria-label="Choisir les machines affichées"
                    sx={(theme) => ({
                        flex: "0 0 60px",
                        minHeight: 76,
                        flexDirection: "column",
                        gap: 0.25,
                        border: "2px dashed",
                        borderColor: "grey.400",
                        borderRadius: 2,
                        color: "text.secondary",
                        fontSize: 10.5,
                        fontWeight: 600,
                        "&.Mui-focusVisible": {
                            outline: `3px solid ${theme.palette.primary.main}`,
                            outlineOffset: 2,
                        },
                    })}
                >
                    <PushPin fontSize="small"/>
                    Machines
                </ButtonBase>
            </Box>

            <DockPicker open={openPicker} onClose={() => setOpenPicker(false)} machines={machines}/>

            <Snackbar
                open={Boolean(errorMessage)}
                autoHideDuration={5000}
                onClose={() => setErrorMessage(null)}
                anchorOrigin={{vertical: "bottom", horizontal: "center"}}
            >
                <Alert onClose={() => setErrorMessage(null)} severity="error" variant="filled" sx={{width: "100%"}}>
                    {errorMessage}
                </Alert>
            </Snackbar>
        </Paper>
    )
}
